import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Address } from "viem";
import { getInjected } from "./lib/chain";

interface WalletState {
  account?: Address;
  chainId?: number;
  connecting: boolean;
  hasWallet: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
}

const WalletCtx = createContext<WalletState | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const injected = getInjected();
  const [account, setAccount] = useState<Address | undefined>();
  const [chainId, setChainId] = useState<number | undefined>();
  const [connecting, setConnecting] = useState(false);

  const refreshChain = useCallback(async () => {
    if (!injected) return;
    const id = (await injected.request({ method: "eth_chainId" })) as string;
    setChainId(parseInt(id, 16));
  }, [injected]);

  const connect = useCallback(async () => {
    if (!injected) throw new Error("No injected wallet found. Install MetaMask or a compatible wallet.");
    setConnecting(true);
    try {
      const accounts = (await injected.request({ method: "eth_requestAccounts" })) as Address[];
      setAccount(accounts[0]);
      await refreshChain();
    } finally {
      setConnecting(false);
    }
  }, [injected, refreshChain]);

  const disconnect = useCallback(() => setAccount(undefined), []);

  useEffect(() => {
    if (!injected) return;
    // Reconnect silently if already authorized.
    injected.request({ method: "eth_accounts" }).then((accts) => {
      const list = accts as Address[];
      if (list.length > 0) {
        setAccount(list[0]);
        void refreshChain();
      }
    });
    const onAccounts = (accts: unknown) => setAccount((accts as Address[])[0]);
    const onChain = (id: unknown) => setChainId(parseInt(id as string, 16));
    const provider = injected as unknown as {
      on?: (e: string, cb: (v: unknown) => void) => void;
      removeListener?: (e: string, cb: (v: unknown) => void) => void;
    };
    provider.on?.("accountsChanged", onAccounts);
    provider.on?.("chainChanged", onChain);
    return () => {
      provider.removeListener?.("accountsChanged", onAccounts);
      provider.removeListener?.("chainChanged", onChain);
    };
  }, [injected, refreshChain]);

  const value = useMemo<WalletState>(
    () => ({ account, chainId, connecting, hasWallet: Boolean(injected), connect, disconnect }),
    [account, chainId, connecting, injected, connect, disconnect],
  );

  return <WalletCtx.Provider value={value}>{children}</WalletCtx.Provider>;
}

export function useWallet(): WalletState {
  const ctx = useContext(WalletCtx);
  if (!ctx) throw new Error("useWallet must be used within WalletProvider");
  return ctx;
}
